import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import themeClasses from 'webapp/helpers/theme-classes';
import didUpdate from 'webapp/modifiers/did-update';
import CustomQuerySetupApiMetricBuilderBodyComponent from 'webapp/components/custom-query/setup/api-metric-builder/body';
import CustomQuerySetupApiMetricBuilderParamsOrHeadersListComponent from 'webapp/components/custom-query/setup/api-metric-builder/params-or-headers-list';

import type CustomMetricSetupFormApiMetricBuilder from 'webapp/models-custom/custom-metric-setup-form/api-metric-builder';
import type HeaderOrQueryParameter from 'webapp/models-custom/custom-metric-setup-form/api-metric-builder/header-or-query-parameter';

interface Args {
	form: CustomMetricSetupFormApiMetricBuilder;
	isDisabled: boolean;
	theme: string;
	onContentChange: () => void;
}

enum Tab {
	PARAMS = 1,
	HEADERS = 2,
	BODY = 3,
}

export default class CustomQuerySetupApiMetricBuilderTabsComponent extends Component<Args> {
	@tracked selectedTab = 1;

	get shownTabs(): SomeDataObject[] {
		const tabs: SomeDataObject[] = [
			{ id: Tab.PARAMS, text: 'parameters', isSelected: this.isTabParamsSelected },
			{ id: Tab.HEADERS, text: 'headers', isSelected: this.isTabHeadersSelected },
		];
		if (this.args.form.isBodySupported) {
			tabs.push({ id: Tab.BODY, text: 'json body', isSelected: this.isTabBodySelected });
		}
		return tabs;
	}

	get isTabParamsSelected(): boolean {
		return this.selectedTab === Tab.PARAMS;
	}

	get isTabHeadersSelected(): boolean {
		return this.selectedTab === Tab.HEADERS;
	}

	get isTabBodySelected(): boolean {
		return this.selectedTab === Tab.BODY;
	}

	@action
	addItem(prop: 'queryParameters' | 'headers', item: HeaderOrQueryParameter): void {
		this.args.form[prop] = [...this.args.form[prop], item];
	}

	@action
	removeItem(prop: 'queryParameters' | 'headers', item: HeaderOrQueryParameter): void {
		this.args.form[prop] = this.args.form[prop].filter((i) => i !== item);
	}

	@action
	openTab(tabId: number): void {
		this.selectedTab = tabId;
	}

	@action
	isBodySupportedChanged(): void {
		if (!this.args.form.isBodySupported && this.isTabBodySelected) {
			this.openTab(Tab.PARAMS);
		}
	}
	<template>
		<div class="row row--tabs">
			<ul class="modalTabs-2">
				{{#each this.shownTabs as |tab|}}
					<li
						class="navigation-button
							{{themeClasses @theme}}
							{{if tab.isSelected 'navigation-button--selected'}}"
					>
						<a role="button" {{on "click" (fn this.openTab tab.id)}}>{{tab.text}}</a>
					</li>
				{{/each}}
			</ul>

			<div class="content" {{didUpdate this.isBodySupportedChanged @form.isBodySupported}}>
				<div class="tab {{if this.isTabParamsSelected 'tab--active'}}">
					<CustomQuerySetupApiMetricBuilderParamsOrHeadersListComponent
						@form={{@form}}
						@model={{@form.queryParameters}}
						@isDisabled={{@isDisabled}}
						@onContentChange={{@onContentChange}}
						@addItem={{fn this.addItem "queryParameters"}}
						@removeItem={{fn this.removeItem "queryParameters"}}
						@theme={{@theme}}
					/>
				</div>

				<div class="tab {{if this.isTabHeadersSelected 'tab--active'}}">
					<CustomQuerySetupApiMetricBuilderParamsOrHeadersListComponent
						@form={{@form}}
						@model={{@form.headers}}
						@isDisabled={{@isDisabled}}
						@onContentChange={{@onContentChange}}
						@addItem={{fn this.addItem "headers"}}
						@removeItem={{fn this.removeItem "headers"}}
						@theme={{@theme}}
					/>
				</div>

				{{#if @form.isBodySupported}}
					<div class="tab {{if this.isTabBodySelected 'tab--active'}}">
						<CustomQuerySetupApiMetricBuilderBodyComponent
							@form={{@form}}
							@isDisabled={{@isDisabled}}
							@onContentChange={{@onContentChange}}
							@theme={{@theme}}
						/>
					</div>
				{{/if}}

			</div>
		</div>
	</template>
}
